<template>
  <TransitionGroup
    name="appear"
    data-test="profile-container"
  >
    <!-- Host not found -->
    <NotFound v-if="hostNotFound" />

    <!-- Loader-->
    <div
      v-else-if="
        !donorStore.hosts_id ||
        (authStore.authenticated && !donorStore.isDataLoaded) ||
        companyAdminStore.companyLoading
      "
      class="flex grow absolute inset-x-0 inset-y-0"
    >
      <div class="flex grow items-center justify-center w-full">
        <div class="flex flex-row items-center justify-center h-full">
          <div class="loader"></div>
        </div>
      </div>
    </div>

    <!-- Main container -->
    <StepsTakeover
      v-else
      :slideOut="slideOut"
      :slideIn="slideIn"
    >
      <!-- Top bar -->
      <template #top>
        <div class="host-image p-1 pl-4 h-[72px] flex justify-center">
          <img
            class="h-full object-contain"
            :src="donorStore.hostLogo"
            :alt="donorStore.hostName"
          />
        </div>
        <div class="mr-6">
          <Button
            class="hidden lg:block !text-sm"
            variant="link"
            as="a"
            :href="donorStore.hostUrl"
          >
            Return to Gives Day Home
          </Button>
        </div>
      </template>

      <!-- Bottom bar -->
      <template #bottom>
        <div class="flex flex-row items-center">
          <Transition name="fade">
            <Button
              v-show="step > 1"
              severity="secondary"
              :disabled="loading"
              label="Back"
              @click="back()"
              class="px-12"
            />
          </Transition>
        </div>
        <div class="flex flex-row items-center gap-4">
          <TransitionGroup name="fade">
            <Button
              key="b-1"
              v-show="step == 3"
              variant="outlined"
              label="Skip for Now"
              @click="next()"
              class="px-12"
            />
            <Button
              key="b-2"
              :disabled="!activeFormValid || loading"
              :loading="loading"
              :label="nextLabel"
              @click="next()"
              class="px-12"
            />
          </TransitionGroup>
        </div>
      </template>

      <!-- Content container -->
      <template #main>
        <!-- Login -->
        <div
          v-show="!authStore.authenticated || authStore.type == 'donorAccount'"
          class="flex flex-col gap-6"
        >
          <h1 class="text-5xl font-bold text-primary">Welcome Back!</h1>
          <h2 class="text-2xl">Please log in to complete your company giving profile.</h2>
          <LoginForm
            @submit="next"
            data-test="login-form"
          />
        </div>
        <!-- Logged in, no profile submitted -->
        <template
          v-if="
            authStore.authenticated &&
            authStore.type == 'companyAccount' &&
            donorStore.isDataLoaded &&
            !companyAdminStore.companyLoading
          "
        >
          <div
            v-if="authStore.authenticated && companyAdminStore.noProfile"
            class="flex flex-col gap-6"
            data-test="no-profile"
          >
            <h1 class="text-5xl font-bold text-primary">Company Giving</h1>
            <h2 class="text-2xl">No profile found</h2>
            <p class="text-base max-w-xs sm:max-w-[430px]">
              You have not yet submitted a company profile for this donor account. If you wish to submit one, you may
              proceed to the next step.
            </p>
          </div>

          <!-- Logged in, profile pending -->
          <div
            v-else-if="authStore.authenticated && companyAdminStore.existingPendingApproval"
            class="flex flex-col gap-6"
            data-test="pending-profile"
          >
            <h1 class="text-5xl font-bold text-primary">Company Giving</h1>
            <h2 class="text-2xl">Profile awaiting approval</h2>
            <p class="text-base max-w-xs sm:max-w-[430px]">
              A company profile has already been submitted for this donor account and is under review. We will notify
              you when your company profile has been approved.
            </p>
          </div>

          <!-- Logged in, profile approved or active -->
          <div
            v-else-if="authStore.authenticated"
            class="flex flex-col gap-6 mb-6"
            data-test="profile-form"
          >
            <div class="flex justify-between mb-6 flex-col sm:flex-row sm:items-center">
              <h1 class="text-5xl font-bold text-primary">
                {{ companyAdminStore.company?.status == 6433 ? 'Complete' : 'Update' }} Your Profile
              </h1>
              <p class="text-base mt-2 sm:mt-0">Step {{ step }}/4</p>
            </div>
            <div
              class="max-w-xs sm:max-w-[430px]"
              v-if="companyAdminStore.company?.companyGiving_id"
            >
              <!-- Step 1-->
              <div
                v-show="step == 1"
                class="flex flex-col gap-6"
              >
                <h2 class="text-2xl">&ldquo;About Us&rdquo;</h2>
                <p>Tell us about your company. The information provided here will appear on your public page.</p>
                <AboutUsForm />
              </div>

              <!-- Step 2 -->
              <div
                v-show="step == 2"
                class="flex flex-col gap-6"
              >
                <h2 class="text-2xl">Gives Day Information</h2>
                <p>
                  Set a donation goal, pick a background image for your page, and tell us a bit more about why working
                  with {{ donorStore.hostName }} matters to you!
                  <!-- Only show if the user is completing the profile for the first time-->
                  <span v-show="companyAdminStore.existingPendingProfile"
                    >You may update this information at any time on your company giving portal.</span
                  >
                </p>
                <GivesDayForm />
                <h2 class="text-2xl">Employee Giving</h2>
                <p>Select the type of verification you’d like for employee donations.</p>
                <EmployeeGivingForm />
              </div>

              <!-- Step 3 -->
              <div
                v-show="step == 3"
                class="flex flex-col gap-6"
              >
                <h2 class="text-2xl">Featured Causes</h2>
                <p>
                  Select a few organizations that are particularly important to your company to be highlighted on your
                  company giving profile!
                  <!-- Only show if the user is completing the profile for the first time-->
                  <span v-show="companyAdminStore.existingPendingProfile">
                    (You can always change this later from your company giving portal.)
                  </span>
                </p>
                <FeaturedCausesForm />
                <h2 class="text-2xl">Media</h2>
                <p>Add some photos to your profile to create a deeper connection with donors.</p>
                <MediaForm />
              </div>

              <!-- Step 4 -->
              <div
                v-if="step == 4"
                class="flex flex-col gap-6"
              >
                <h2 class="text-2xl">Review your Information</h2>
                <p>Please take a moment to review your information before submitting your registration.</p>
                <FormReview @back="back" />
              </div>
            </div>
          </div>
        </template>
      </template>
    </StepsTakeover>
  </TransitionGroup>
</template>

<script setup>
import { ref, onBeforeMount, computed, watchEffect, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useToastStore } from '@/stores/ToastStore.js';

import LoginForm from '@/components/auth/LoginForm.vue';
import StepsTakeover from '@/components/StepsTakeover.vue';
import NotFound from '@/pages/NotFound.vue';
import AboutUsForm from '@/components/companyGiving/AboutUsForm.vue';
import EmployeeGivingForm from '@/components/companyGiving/EmployeeGivingForm.vue';
import FeaturedCausesForm from '@/components/companyGiving/FeaturedCausesForm.vue';
import GivesDayForm from '@/components/companyGiving/GivesDayForm.vue';
import MediaForm from '@/components/companyGiving/MediaForm.vue';
import FormReview from '@/components/companyGiving/FormReview.vue';

const authStore = useAuthStore();
const companyAdminStore = useCompanyAdminStore();
const donorStore = useDonorStore();
const router = useRouter();
const hostNotFound = ref(null);
const step = ref(0);
const loading = ref(false);
const slideOut = ref(false);
const slideIn = ref(false);

const activeFormValid = computed(() => {
  if (step.value == 0) {
    return authStore.loginForm?.valid;
  }

  return true;
});

const nextLabel = computed(() => {
  if (step.value == 1 && companyAdminStore.existingPendingApproval) {
    return 'Return to Donor Portal';
  } else if (step.value == 4) {
    return 'Submit';
  }
  return 'Next';
});

onBeforeMount(async () => {
  try {
    await donorStore.fetchHostFromPortalDomain();
    await authStore.checkLocalStorage();

    if (authStore.authenticated && authStore.type == 'companyAccount') {
      step.value = 1;
    }
  } catch (error) {
    console.error(error);
    hostNotFound.value = true;
  }
});

const back = (by = 1) => {
  if (loading.value) return;

  if (step.value < 2) {
    // Return to company giving home, or portal home
    if (!companyAdminStore.existingPendingApproval && !companyAdminStore.noProfile) {
      router.push('/company/overview');
    } else {
      router.push('/');
    }
    return;
  }

  if (step.value > 1) {
    // Animated transition in StepsTakeover
    requestAnimationFrame(() => {
      slideOut.value = false;
      slideIn.value = true;
      loading.value = true;
      setTimeout(() => {
        step.value -= parseInt(by);
        slideIn.value = false;
        loading.value = false;
      }, 500);
    });
  }
};

const next = async () => {
  if (loading.value) return;
  loading.value = true;

  try {
    switch (step.value) {
      // Log in
      case 0:
        await authStore.donorLogin({ companyAccount: true });
        break;

      case 1:
        // Redirect to donor portal if profile is pending approval
        if (companyAdminStore.existingPendingApproval) {
          router.push('/');
          return;
        }

        // Redirect to registration if no profile exists
        if (companyAdminStore.noProfile) {
          router.push('/company/registration');
          return;
        }
        break;

      case 4:
        const prevPending = companyAdminStore.company.status == 6433;
        await companyAdminStore.saveProfile();

        useToastStore().add({
          severity: 'success',
          summary: 'Profile ' + (prevPending ? 'Created' : 'Updated'),
          detail: 'Your company profile has been ' + (prevPending ? 'created' : 'updated') + ' successfully.',
          life: 3000,
        });

        slideOut.value = true;
        slideIn.value = false;
        router.push('/company/overview');
        return;
    }
  } catch (e) {
    // Report error as a Toast notification
    useToastStore().add({
      severity: 'error',
      summary: 'Error',
      detail: e.message,
      life: 3000,
    });
    loading.value = false;
    return;
  }

  // Animated transition in StepsTakeover
  requestAnimationFrame(() => {
    slideOut.value = true;
    slideIn.value = false;
    setTimeout(() => {
      step.value++;
      loading.value = false;
      slideOut.value = false;
    }, 500);
  });
};
</script>
